import { ThemeEvents } from '@theme/events';

class ProductMobileStickyBar extends HTMLElement {
  #intersectionObserver;
  #resizeObserver;

  connectedCallback() {
    this.section = this.closest('.shopify-section');
    if (!this.section) return;

    this.cta = this.querySelector('[ref="cta"]');
    this.cta?.addEventListener('click', this.handleCtaClick);
    this.section.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);

    this.#setupVisibilityObserver();
    this.#setupTitleMarquee();
  }

  disconnectedCallback() {
    this.cta?.removeEventListener('click', this.handleCtaClick);
    this.section?.removeEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
    this.#intersectionObserver?.disconnect();
    this.#resizeObserver?.disconnect();
  }

  handleCtaClick = (event) => {
    event.preventDefault();
    const target = this.section?.querySelector('add-to-cart-component [ref="addToCartButton"]');
    if (target && !target.disabled) target.click();
  };

  handleVariantUpdate = (event) => {
    const newProduct = event.detail.data.newProduct;
    if (newProduct) {
      this.dataset.productId = newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newHtml = event.detail.data.html;
    const newAtcButton = newHtml?.querySelector('add-to-cart-component [ref="addToCartButton"]');
    if (!newAtcButton || !this.cta) return;

    const isDisabled = newAtcButton.disabled || newAtcButton.hasAttribute('disabled');
    this.cta.disabled = isDisabled;

    const labelEl = this.querySelector('[ref="ctaLabel"]');
    const newLabel = newAtcButton.querySelector('.add-to-cart-text__content')?.textContent.trim();
    if (labelEl && newLabel) labelEl.textContent = newLabel;

    const iconEl = this.querySelector('[ref="ctaIcon"]');
    if (iconEl) iconEl.style.display = isDisabled ? 'none' : '';

    // Mirror the variant-specific media URL exposed by add-to-cart-component
    const newMediaUrl = newAtcButton.closest('add-to-cart-component')?.dataset.productVariantMedia;
    const thumb = this.querySelector('[ref="thumb"]');
    if (thumb && newMediaUrl && thumb.src !== newMediaUrl) {
      thumb.src = newMediaUrl;
    }
  };

  #setupVisibilityObserver() {
    const target = this.section?.querySelector('.product-form-buttons');
    if (!target) return;

    this.#intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        this.dataset.hidden = entry.isIntersecting ? 'true' : 'false';
      },
      { threshold: 0.1 }
    );
    this.#intersectionObserver.observe(target);
  }

  #setupTitleMarquee() {
    const titleViewport = this.querySelector('[ref="title"]');
    const titleText = this.querySelector('[ref="titleText"]');
    if (!titleViewport || !titleText) return;

    const updateMarquee = () => {
      // Measure with marquee off so padding-right doesn't inflate scrollWidth
      titleViewport.classList.remove('is-marquee');
      const viewportWidth = titleViewport.clientWidth;
      const textWidth = titleText.scrollWidth;
      const overflows = textWidth > viewportWidth + 1;
      titleViewport.classList.toggle('is-marquee', overflows);

      if (overflows) {
        // Roughly 40px/sec — clamps so very short overflows don't whip past
        const duration = Math.max(8, Math.round((textWidth + 24) / 40));
        titleViewport.style.setProperty('--pms-marquee-duration', `${duration}s`);
      } else {
        titleViewport.style.removeProperty('--pms-marquee-duration');
      }
    };

    // Initial measure (after fonts/layout settle)
    requestAnimationFrame(updateMarquee);

    this.#resizeObserver = new ResizeObserver(updateMarquee);
    this.#resizeObserver.observe(titleViewport);
  }
}

if (!customElements.get('product-mobile-sticky-bar')) {
  customElements.define('product-mobile-sticky-bar', ProductMobileStickyBar);
}
